import { Button, Group, Modal, Text } from '@mantine/core';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';

interface Props {
	opened: boolean;
	secondsRemaining: number;
	isExtending: boolean;
	onStaySignedIn: () => void;
	onSignOut: () => void;
}

const formatCountdown = (totalSeconds: number): string => {
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;

	return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Deliberately not dismissible by clicking away or pressing escape: dismissing would look
 * like "carry on", but the session would still expire.
 */
export const SessionTimeoutWarningModal = ({
	opened,
	secondsRemaining,
	isExtending,
	onStaySignedIn,
	onSignOut,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();

	return (
		<Modal
			opened={opened}
			onClose={onStaySignedIn}
			withCloseButton={false}
			closeOnClickOutside={false}
			closeOnEscape={false}
			centered
			title={tCommonUi('session_timeout.title', {
				defaultValue: 'Still there?',
			})}
			data-testid={TEST_ELEMENT_IDS.SESSION_TIMEOUT_WARNING_MODAL}
		>
			<Text size="B5Medium" mb="1rem">
				{tCommonUi('session_timeout.description', {
					defaultValue:
						'For your security you will be signed out in {{countdown}} because of inactivity. Anything you have not saved will be lost.',
					countdown: formatCountdown(secondsRemaining),
				})}
			</Text>
			<Group justify="flex-end">
				<Button
					variant="outline"
					onClick={onSignOut}
					data-testid={TEST_ELEMENT_IDS.SESSION_TIMEOUT_SIGN_OUT_BUTTON}
				>
					{tCommonUi('session_timeout.sign_out', { defaultValue: 'Sign out now' })}
				</Button>
				<Button
					onClick={onStaySignedIn}
					loading={isExtending}
					data-testid={TEST_ELEMENT_IDS.SESSION_TIMEOUT_STAY_SIGNED_IN_BUTTON}
				>
					{tCommonUi('session_timeout.stay_signed_in', {
						defaultValue: 'Stay signed in',
					})}
				</Button>
			</Group>
		</Modal>
	);
};
